/* global globalThis */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import Sidebar from './components/Sidebar';
import MainEditor from './components/Editor';
import AIChat from './components/AIChat';
import TopNavbar from './components/TopNavbar';
import axiosClientPayload from '../../../api-client/AxiosClientPayload';
import { showToast } from '../../common/Toaster';
import * as Diff from 'diff';
import './styles/Scriptly.css';


const SCRIPTLY_API_BASE_CC = process.env.REACT_APP_API_BASE_URL_CC;

const isRuleObject = (target) => {
  if (!target || typeof target !== 'object') return false;
  const keys = Object.keys(target).map(k => k.toLowerCase());
  return keys.some(k => k.includes('join') || k.includes('condition') || k.includes('script'));
};

const getRuleValue = (target, ...keys) => {
  for (const k of keys) {
    const foundKey = Object.keys(target).find(pk => pk.toLowerCase() === k.toLowerCase());
    if (foundKey) return target[foundKey];
  }
  return '-- None';
};

const formatRuleContent = (tableName, rule) =>
  `-- TARGET TABLE: ${tableName}\n\n` +
  `/* PRE-SCRIPT */\n${getRuleValue(rule, 'pre_script', 'preScript')}\n\n` +
  `/* JOIN CONDITION */\n${getRuleValue(rule, 'join_condition', 'joinCondition')}\n\n` +
  `/* WHERE CONDITION */\n${getRuleValue(rule, 'where_condition', 'whereCondition')}\n\n` +
  `/* COLUMN TRANSFORMATIONS */\n${getRuleValue(rule, 'column_condition', 'columnCondition', 'column_transformations')}`;

const processRuleEntry = (key, value, parsedRules, recurse) => {
  const childrenKeys = Object.keys(value);
  const firstChild = value[childrenKeys[0]];

  if (isRuleObject(firstChild)) {
    childrenKeys.forEach(tableName => {
      parsedRules.push({ name: tableName, content: formatRuleContent(tableName, value[tableName]) });
    });
  } else if (isRuleObject(value)) {
    parsedRules.push({ name: key, content: formatRuleContent(key, value) });
  } else {
    recurse(value);
  }
};



const parseStringData = (data) => {
  try {
    const p = JSON.parse(data);
    if (Array.isArray(p)) return p;
    if (p.data && Array.isArray(p.data)) return p.data;
    if (p.result && Array.isArray(p.result)) return p.result;
  } catch (e) {
    console.warn("Scriptly parse error:", e);
  }
  return [];
};

const extractDocs = (data) => {
  if (Array.isArray(data)) return data;
  if (!data) return [];
  if (typeof data === 'string') return parseStringData(data);

  const arrayKeys = ['mapping_documents', 'data', 'result', 'items', 'Documents'];
  for (const key of arrayKeys) {
    if (Array.isArray(data[key])) return data[key];
  }

  return [];
};

const isDdlOrDmlKey = (key) => {
  const k = String(key || '').toLowerCase();
  return k === 'ddl' || k === 'dml';
};

/** For script_type Both, API returns ddl/dml nested keys — use TABLE_DDL / TABLE_DML tab names. */
const getScriptTabName = (tableName, typeKey, scriptType) => {
  if (scriptType === 'Both' && isDdlOrDmlKey(typeKey)) {
    return `${tableName}_${String(typeKey).toUpperCase()}`;
  }
  return tableName;
};

const parseGeneratedScripts = (scriptsObj, scriptType) => {
  const parsedScripts = [];

  for (const mappingId in scriptsObj) {
    if (!Object.prototype.hasOwnProperty.call(scriptsObj, mappingId)) continue;
    const types = scriptsObj[mappingId];
    if (!types || typeof types !== 'object' || Array.isArray(types)) continue;

    const mappingKeys = Object.keys(types);
    const hasDdlDmlNesting = mappingKeys.some(isDdlOrDmlKey);

    if (hasDdlDmlNesting) {
      mappingKeys.filter(isDdlOrDmlKey).forEach((typeKey) => {
        const scriptsMap = types[typeKey];
        if (!scriptsMap || typeof scriptsMap !== 'object') return;
        Object.keys(scriptsMap).forEach((tableName) => {
          const content = scriptsMap[tableName];
          if (typeof content !== 'string') return;
          parsedScripts.push({
            name: getScriptTabName(tableName, typeKey, scriptType),
            content,
          });
        });
      });
    } else {
      mappingKeys.forEach((tableName) => {
        const content = types[tableName];
        if (typeof content === 'string') {
          parsedScripts.push({ name: tableName, content });
        }
      });
    }
  }

  return parsedScripts;
};

const GenerateScript = () => {
  const [scLayer, setScLayer] = useState('Transform');
  const [scScriptType, setScScriptType] = useState('DDL');
  const [scType, setScType] = useState('SQL');
  const [scDefaultAction, setScDefaultAction] = useState('Create');
  const [scSafeScripts, setScSafeScripts] = useState(false);
  const [scQuoteOption, setScQuoteOption] = useState('Backticks');

  const [scSelectedObjects] = useState(new Set());
  const [scGeneratedSQL, setScGeneratedSQL] = useState(null);
  const [scGeneratedScripts, setScGeneratedScripts] = useState([]);
  const [scTransformationRules, setScTransformationRules] = useState([]);
  const [scReviewSummary, setScReviewSummary] = useState(null);
  const [scGenerating, setScGenerating] = useState(false);

  const [scMappingDocs, setScMappingDocs] = useState([]);
  const [scMappingDocsLoading, setScMappingDocsLoading] = useState(false);
  const [scSelectedMappings, setScSelectedMappings] = useState([]);
  const [scSavedScriptsResponse, setScSavedScriptsResponse] = useState(null);
  const [scFetchingSavedScripts, setScFetchingSavedScripts] = useState(false);
  const [scSelectedFiles, setScSelectedFiles] = useState([]);
  const [scPreviewing, setScPreviewing] = useState(false);

  const [scAiSidebarWidth, setScAiSidebarWidth] = useState(300);
  const [isResizingAi, setIsResizingAi] = useState(false);
  const [scAiPanelOpen, setScAiPanelOpen] = useState(false);

  const startAiResizing = useCallback((e) => {
    setIsResizingAi(true);
    e.preventDefault();
  }, []);

  const stopAiResizing = useCallback(() => {
    setIsResizingAi(false);
  }, []);

  const doAiResizing = useCallback((e) => {
    if (isResizingAi) {
      const newWidth = globalThis.innerWidth - e.clientX;
      if (newWidth > 250 && newWidth < 800) {
        setScAiSidebarWidth(newWidth);
      }
    }
  }, [isResizingAi]);

  useEffect(() => {
    if (isResizingAi) {
      globalThis.addEventListener('mousemove', doAiResizing);
      globalThis.addEventListener('mouseup', stopAiResizing);
    } else {
      globalThis.removeEventListener('mousemove', doAiResizing);
      globalThis.removeEventListener('mouseup', stopAiResizing);
    }
    return () => {
      globalThis.removeEventListener('mousemove', doAiResizing);
      globalThis.removeEventListener('mouseup', stopAiResizing);
    };
  }, [isResizingAi, doAiResizing, stopAiResizing]);

  // Query State
  const [scQueryRunning, setScQueryRunning] = useState(false);
  const [scQueryResult, setScQueryResult] = useState(null);

  // AI State
  const [scAiInput, setScAiInput] = useState('');
  const [scAiMessages, setScAiMessages] = useState([]);
  const [scAiLoading, setScAiLoading] = useState(false);

  // UI State
  const [activeScriptTab, setActiveScriptTab] = useState(null);
  const [scViewMode, setScViewMode] = useState('code');
  const [scPendingChange, setScPendingChange] = useState(null);

  // Table Fields State
  const [scTableFields, setScTableFields] = useState(null);
  const [scSelectedTables, setScSelectedTables] = useState([]);

  const scTableList = useMemo(() => {
    let list = [];
    if (scTableFields?.results) {
      Object.keys(scTableFields.results).forEach(mappingId => {
        const mappingInfo = scTableFields.results[mappingId];
        if (mappingInfo?.status === 'success') {
          if (scLayer === 'Extract') {
            const sources = mappingInfo.discovery?.source_tables_for_db || [];
            sources.forEach(t => {
              if (!list.includes(t)) list.push(t);
            });
          } else {
            const targets = mappingInfo.discovery?.target_tables_normalized || [];
            targets.forEach(t => {
              if (!list.includes(t)) list.push(t);
            });
          }
        }
      });
    }
    return list;
  }, [scLayer, scTableFields]);

  useEffect(() => {
    setScSelectedTables([]);
  }, [scTableList]);

  const [extractInstance, setExtractInstance] = useState('Loading...');
  const [transformInstance, setTransformInstance] = useState('Loading...');
  const [organizationConfigId, setOrganizationConfigId] = useState(
    () => localStorage.getItem("organization_config_id")
  );
  const isInitialOrgConfigMount = useRef(true);

  const resetScriptlyWorkState = useCallback(() => {
    setScGeneratedSQL(null);
    setScGeneratedScripts([]);
    setScTransformationRules([]);
    setScReviewSummary(null);
    setScGenerating(false);
    setScMappingDocs([]);
    setScMappingDocsLoading(false);
    setScSelectedMappings([]);
    setScSavedScriptsResponse(null);
    setScFetchingSavedScripts(false);
    setScSelectedFiles([]);
    setScPreviewing(false);
    setScQueryRunning(false);
    setScQueryResult(null);
    setScAiInput('');
    setScAiMessages([]);
    setScAiLoading(false);
    setActiveScriptTab(null);
    setScViewMode('code');
    setScPendingChange(null);
    setScTableFields(null);
    setScSelectedTables([]);
    setExtractInstance('Loading...');
    setTransformInstance('Loading...');
  }, []);

  useEffect(() => {
    const applyOrgConfigId = (newConfigId) => {
      setOrganizationConfigId((prev) => (prev === newConfigId ? prev : newConfigId));
    };

    const handleCustomStorageChange = (e) => {
      if (e.detail?.key === "organization_config_id") {
        applyOrgConfigId(e.detail.newValue || null);
      }
    };

    const handleStorageChange = (e) => {
      if (e.key === "organization_config_id") {
        applyOrgConfigId(e.newValue || null);
      }
    };

    globalThis.addEventListener("customStorageChange", handleCustomStorageChange);
    globalThis.addEventListener("storage", handleStorageChange);

    return () => {
      globalThis.removeEventListener("customStorageChange", handleCustomStorageChange);
      globalThis.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (isInitialOrgConfigMount.current) {
      isInitialOrgConfigMount.current = false;
      return;
    }
    resetScriptlyWorkState();
  }, [organizationConfigId, resetScriptlyWorkState]);

  useEffect(() => {
    const fetchEtlConfig = async () => {
      if (!organizationConfigId) {
        setExtractInstance('Not Configured');
        setTransformInstance('Not Configured');
        return;
      }

      try {
        const response = await axiosClientPayload.get(
          `${SCRIPTLY_API_BASE_CC}/api/etl-configuration?organizationConfigId=${organizationConfigId}`
        );

        if (response.data?.status === 'success' && Array.isArray(response.data?.data)) {
          const extractConfig = response.data.data.find(c => c.etl_type === 'extract');
          const transformConfig = response.data.data.find(c => c.etl_type === 'transform');

          setExtractInstance(extractConfig ? extractConfig.instance_name : 'Not Configured');
          setTransformInstance(transformConfig ? transformConfig.instance_name : 'Not Configured');
        } else {
          setExtractInstance('Not Configured');
          setTransformInstance('Not Configured');
        }
      } catch (err) {
        console.error("Failed to fetch ETL configuration:", err);
        setExtractInstance('Error');
        setTransformInstance('Error');
      }
    };

    fetchEtlConfig();
  }, [organizationConfigId]);

  const fetchMappingDocs = useCallback(async () => {
    setScMappingDocsLoading(true);
    try {
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/mapping-documents`,
        { organization_config_id: organizationConfigId }
      );

      setScMappingDocs(extractDocs(response.data));
    } catch (err) {
      console.error("Failed to fetch mapping documents:", err);
    } finally {
      setScMappingDocsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!organizationConfigId) {
      setScMappingDocs([]);
      return;
    }
    fetchMappingDocs();
  }, [organizationConfigId, fetchMappingDocs]);

  useEffect(() => {
    const fetchTableFields = async () => {
      if (!scSelectedMappings || scSelectedMappings.length === 0) {
        setScTableFields(null);
        return;
      }
      try {
        const organizationConfigId = localStorage.getItem("organization_config_id") || "";
        const token = localStorage.getItem("token") || "";
        const mappingFileIds = scSelectedMappings.map(m => m.id || m._id);

        const payload = {
          organization_config_id: organizationConfigId,
          mapping_file_id: mappingFileIds,
          token: token
        };

        const response = await axiosClientPayload.post(
          `${SCRIPTLY_API_BASE_CC}/api/get-table-fields`,
          payload
        );

        setScTableFields(response.data);
      } catch (error) {
        console.error("Failed to fetch table fields:", error);
      }
    };
    fetchTableFields();
  }, [scSelectedMappings]);

  const handleGenerateSQL = useCallback(async () => {
    if (!scSelectedMappings || scSelectedMappings.length === 0) {
      showToast("Selection Required", "Please select at least one mapping document to generate a script.", "warn");
      return;
    }

    setScSavedScriptsResponse(null);
    setScSelectedFiles([]);
    setScGenerating(true);
    try {
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      const token = localStorage.getItem("token") || "";
      const mappingFileIds = scSelectedMappings.map(m => m.id || m._id);

      const payload = {
        token: token,
        organization_config_id: organizationConfigId,
        mapping_file_id: mappingFileIds,
        script_type: scScriptType,
        operation: scLayer,
        language: scType,
        session_id: "",
        table_names: scSelectedTables
      };

      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/generate-script`,
        payload
      );

      // Parse response to extract scripts
      const scriptsObj = response.data?.scripts || {};
      const responseScriptType = response.data?.script_type || scScriptType;
      const parsedScripts = parseGeneratedScripts(scriptsObj, responseScriptType);


      const rulesObj = response.data?.transformation_rules || {};
      const parsedRules = [];

      const discoverRules = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        if (isRuleObject(obj)) return;
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (value && typeof value === 'object') {
            processRuleEntry(key, value, parsedRules, discoverRules);
          }
        });
      };

      discoverRules(rulesObj);

      setScReviewSummary(response.data?.review_summary || null);
      setScGeneratedScripts(parsedScripts);
      setScTransformationRules(parsedRules);

      // Auto-switch mode if only rules are available
      if (parsedRules.length > 0 && parsedScripts.length === 0) {
        setScViewMode('rules');
      }

      if (parsedScripts.length > 0) {
        setScGeneratedSQL(parsedScripts[0].content);
      } else if (parsedRules.length > 0) {
        setScGeneratedSQL(parsedRules[0].content);
      } else {
        const msg = "-- No scripts returned from API.";
        setScGeneratedSQL(msg);
      }
    } catch (error) {
      console.error("Failed to generate script:", error);
      const errorMsg = error.response?.data?.message || error.message;
      setScGeneratedSQL(`-- Error generating script: ${errorMsg}`);
    } finally {
      setScGenerating(false);
    }
  }, [scSelectedMappings, scScriptType, scLayer, scType, scSelectedTables]);

  const fetchSavedScripts = useCallback(async () => {
    if (!scSelectedMappings || scSelectedMappings.length === 0) {
      showToast("Selection Required", "Please select at least one mapping document to fetch saved scripts.", "warn");
      return;
    }

    setScFetchingSavedScripts(true);
    try {
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      const token = localStorage.getItem("token") || "";
      const mappingFileIds = scSelectedMappings.map(m => m.id || m._id);

      const payload = {
        token: token,
        organization_config_id: organizationConfigId,
        mapping_file_id: mappingFileIds,
        script_type: scScriptType,
        operation: scLayer,
        language: scType,
        session_id: "",
        table_names: scSelectedTables || [],
        share_name: "codecheetah"
      };

      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/saved-scripts-preview`,
        payload
      );

      setScSavedScriptsResponse(response.data);

    } catch (error) {
      console.error("Failed to fetch saved scripts:", error);
      const errorMsg = error.response?.data?.message || error.message;
      showToast("Error", `Failed to fetch saved scripts: ${errorMsg}`, "error");
    } finally {
      setScFetchingSavedScripts(false);
    }
  }, [scSelectedMappings, scLayer, scScriptType, scType, scSelectedTables]);

  const handlePreviewSavedScripts = useCallback(async () => {
    if (!scSelectedFiles || scSelectedFiles.length === 0) {
      showToast("Selection Required", "Please select at least one script version to preview.", "warn");
      return;
    }

    setScPreviewing(true);
    try {
      const organizationConfigId = localStorage.getItem("organization_config_id") || "";
      const token = localStorage.getItem("token") || "";
      const mappingFileIds = scSelectedMappings.map(m => m.id || m._id);

      const payload = {
        token: token,
        organization_config_id: organizationConfigId,
        mapping_file_id: mappingFileIds,
        operation: scLayer,
        script_type: scScriptType,
        language: scType,
        session_id: "",
        table_names: scSelectedTables || [],
        file_names: scSelectedFiles,
        share_name: "codecheetah"
      };

      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/saved-scripts-preview`,
        payload
      );

      console.log("Preview Script Response:", response.data);
      if (response.data?.status === 'success') {

        const selectedFiles = response.data?.selected_files || [];
        const parsedScripts = selectedFiles.map(file => ({
          name: file.file_name,
          content: file.content
        }));

        setScGeneratedScripts(parsedScripts);
        setScTransformationRules([]);

        if (parsedScripts.length > 0) {
          setScGeneratedSQL(parsedScripts[0].content);

          try {
            const reviewPayload = {
              code: parsedScripts[0].content,
              language: scType || "SQL"
            };
            const reviewResponse = await axiosClientPayload.post(
              `${SCRIPTLY_API_BASE_CC}/api/code-review`,
              reviewPayload
            );


            let reviewData = reviewResponse.data?.review_summary || reviewResponse.data;
            if (reviewData?.sections) {
              Object.keys(reviewData.sections).forEach(secKey => {
                const sec = reviewData.sections[secKey];
                if (sec.tables && sec.tables.length > 0) {
                  sec.tables.forEach(t => { t.table_name = parsedScripts[0].name; });
                }
              });
            }
            reviewData.table_name = parsedScripts[0].name;
            setScReviewSummary(reviewData);
          } catch (reviewError) {
            console.error("Failed to fetch code review:", reviewError);
            setScReviewSummary(null);
          }
        } else {
          setScGeneratedSQL("-- No script content returned.");
          setScReviewSummary(null);
        }
      }
    } catch (error) {
      console.error("Failed to preview scripts:", error);
      const errorMsg = error.response?.data?.message || error.message;
      showToast("Error", `Failed to preview scripts: ${errorMsg}`, "error");
    } finally {
      setScPreviewing(false);
    }
  }, [scSelectedFiles, scSelectedMappings, scLayer, scScriptType, scSelectedTables]);

  const activeScriptCode = useMemo(() => {
    const list = scViewMode === 'code' ? scGeneratedScripts : scTransformationRules;
    if (!list || list.length === 0) return scGeneratedSQL || "";
    const found = list.find(s => s.name === activeScriptTab);
    return found ? found.content : list[0].content;
  }, [activeScriptTab, scGeneratedScripts, scTransformationRules, scGeneratedSQL, scViewMode]);

  const handleRunQuery = useCallback(() => {
    setScQueryRunning(true);
    setTimeout(() => {
      setScQueryResult({
        cols: ['id', 'name', 'status', 'created_at'],
        rows: [
          ['1', 'Alice Johnson', 'active', '2025-01-10 09:23:00'],
          ['2', 'Bob Smith', 'inactive', '2025-01-12 14:05:00'],
          ['3', 'Carol Williams', 'active', '2025-02-03 11:42:00'],
        ],
      });
      setScQueryRunning(false);
    }, 800);
  }, []);

  const handleAiAlter = useCallback(async (prompt) => {
    if (!prompt.trim()) return;
    setScAiLoading(true);
    setScAiMessages(m => [...m, { role: 'user', text: prompt }]);
    setScAiInput('');

    try {
      const payload = {
        code: activeScriptCode,
        query: prompt,
        language: scType,
        mode: "auto",
        conversation_history: null
      };

      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/ai-assist`,
        payload
      );

      const updatedCode = response.data?.modified_code;
      const explanation = response.data?.explanation;
      const statusMessage = response.data?.message;

      if (updatedCode || statusMessage) {
        const messageId = Date.now();
        const isChanged = !!updatedCode && response.data?.changed !== false;
        const patch = isChanged ? Diff.createPatch('script.sql', activeScriptCode, updatedCode) : null;

        if (isChanged) {
          setScPendingChange({
            oldCode: activeScriptCode,
            newCode: updatedCode,
            tableName: activeScriptTab,
            viewMode: scViewMode,
            messageId: messageId,
            patch: patch
          });
        }

        setScAiMessages(m => [...m, {
          role: 'ai',
          id: messageId,
          text: isChanged
            ? "I have analyzed the script and proposed some changes. Please review them in the editor."
            : (statusMessage || "I have analyzed the script. No changes were proposed."),
          explanation: Array.isArray(explanation) ? explanation : (typeof explanation === 'string' ? explanation : null),
          hasPendingChange: isChanged,
          changed: response.data?.changed,
          patch: patch
        }]);
      } else {
        throw new Error("I processed your request but no changes were returned.");
      }
    } catch (error) {
      console.error("AI Assist error:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to process AI request";
      setScAiMessages(m => [...m, { role: 'ai', text: `Sorry, I encountered an error: ${errorMsg}` }]);
    } finally {
      setScAiLoading(false);
    }
  }, [activeScriptCode, scType, activeScriptTab, scGeneratedScripts, scViewMode]);

  const handleFixWithAI = useCallback(async (tableName, issuePoint, scriptType) => {
    // Find the script content for this table
    const targetScript = scGeneratedScripts.find(s => s.name === tableName);
    const codeToFix = targetScript?.content || activeScriptCode || '';

    if (!codeToFix) {
      showToast('No Code', 'No script code found for this table to fix.', 'warn');
      return;
    }

    const fixQuery = `Fix the following issue in the ${tableName} ${scriptType || ''} script: ${issuePoint}`;

    // Show user message in AI Chat (same flow as normal AI assist)
    setScAiMessages(m => [...m, { role: 'user', text: fixQuery }]);
    setScAiInput('');
    setScAiLoading(true);

    try {
      const payload = {
        code: codeToFix,
        query: fixQuery,
        language: scType,
        mode: 'auto',
        conversation_history: null
      };

      const response = await axiosClientPayload.post(
        `${SCRIPTLY_API_BASE_CC}/api/ai-assist`,
        payload
      );

      const updatedCode = response.data?.modified_code;
      const explanation = response.data?.explanation;
      const statusMessage = response.data?.message;

      const messageId = Date.now();
      const isChanged = !!updatedCode && response.data?.changed !== false;
      const patch = isChanged ? Diff.createPatch('script.sql', codeToFix, updatedCode) : null;

      if (isChanged) {
        setScPendingChange({
          oldCode: codeToFix,
          newCode: updatedCode,
          tableName: tableName,
          viewMode: 'code',
          messageId: messageId,
          patch: patch
        });

        // Auto-switch to the target script tab
        setActiveScriptTab(tableName);
        setScViewMode('code');
      }

      // Show AI response in chat
      setScAiMessages(m => [...m, {
        role: 'ai',
        id: messageId,
        text: isChanged
          ? `I have proposed a fix for the issues in ${tableName}. Please review the changes in the editor.`
          : (statusMessage || "I have analyzed the script. No changes were proposed."),
        explanation: Array.isArray(explanation) ? explanation : (typeof explanation === 'string' ? explanation : null),
        hasPendingChange: isChanged,
        changed: response.data?.changed,
        patch: patch
      }]);
    } catch (error) {
      console.error('Fix with AI error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to apply AI fix';
      setScAiMessages(m => [...m, {
        role: 'ai',
        text: `Sorry, I encountered an error: ${errorMsg}`
      }]);
    } finally {
      setScAiLoading(false);
    }
  }, [scGeneratedScripts, activeScriptCode, scType]);

  const handleAcceptChange = useCallback(() => {
    if (!scPendingChange) return;

    try {
      const { newCode, tableName, viewMode } = scPendingChange;

      // 1. Update the actual code state
      if (viewMode === 'rules') {
        setScTransformationRules(prev => {
          if (!Array.isArray(prev)) return prev;
          return prev.map(s => s.name === tableName ? { ...s, content: newCode } : s);
        });
      } else if (Array.isArray(scGeneratedScripts) && scGeneratedScripts.length > 0 && tableName) {
        setScGeneratedScripts(prev => {
          if (!Array.isArray(prev)) return prev;
          return prev.map(s => s.name === tableName ? { ...s, content: newCode } : s);
        });
      } else {
        setScGeneratedSQL(newCode);
      }

      // 2. Clear pending state and update message text
      const messageId = scPendingChange.messageId;
      const updateMessages = prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, text: "Code updated successfully.", hasPendingChange: false } : msg
      );
      setTimeout(() => {
        setScAiMessages(updateMessages);
        setScPendingChange(null);
      }, 10);

      // Dispatch custom event to trigger code quality / review refresh
      globalThis.dispatchEvent(new CustomEvent('scriptlyCodeAccepted', {
        detail: { tableName, newCode }
      }));
    } catch (err) {
      console.error("Error in handleAcceptChange:", err);
      setScPendingChange(null);
    }
  }, [scPendingChange, scGeneratedScripts]);

  const handleRejectChange = useCallback(() => {
    if (!scPendingChange) return;
    const messageId = scPendingChange.messageId;

    const updateMessages = prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, text: "Code changes discarded.", hasPendingChange: false } : msg
    );
    setTimeout(() => {
      setScAiMessages(updateMessages);
      setScPendingChange(null);
    }, 10);
  }, [scPendingChange]);

  return (
    <div className="scriptly-container flex flex-col h-screen bg-[#f8fafc] overflow-hidden relative">
      <TopNavbar
        extractInstance={extractInstance}
        transformInstance={transformInstance}
        handleGenerateSQL={handleGenerateSQL}
        fetchSavedScripts={fetchSavedScripts}
        scSavedScriptsResponse={scSavedScriptsResponse}
        scFetchingSavedScripts={scFetchingSavedScripts}
        handlePreviewSavedScripts={handlePreviewSavedScripts}
        scSelectedFiles={scSelectedFiles}
        setScSelectedFiles={setScSelectedFiles}
        scPreviewing={scPreviewing}
        scGenerating={scGenerating}
        scMappingDocs={scMappingDocs}
        scMappingDocsLoading={scMappingDocsLoading}
        scSelectedMappings={scSelectedMappings}
        setScSelectedMappings={setScSelectedMappings}
        scTableList={scTableList}
        scSelectedTables={scSelectedTables}
        setScSelectedTables={setScSelectedTables}
      />

      <div className="scriptly-main-content flex-1 flex overflow-hidden relative">
        <Sidebar
          scLayer={scLayer} setScLayer={setScLayer}
          scScriptType={scScriptType} setScScriptType={setScScriptType}
          scType={scType} setScType={setScType}
          scDefaultAction={scDefaultAction} setScDefaultAction={setScDefaultAction}
          scSafeScripts={scSafeScripts} setScSafeScripts={setScSafeScripts}
          scQuoteOption={scQuoteOption} setScQuoteOption={setScQuoteOption}
        />

        <MainEditor
          extractInstance={extractInstance}
          transformInstance={transformInstance}
          scLayer={scLayer}
          scTableFields={scTableFields}
          scSelectedObjects={scSelectedObjects}
          scGeneratedSQL={scGeneratedSQL}
          scGeneratedScripts={scGeneratedScripts}
          scTransformationRules={scTransformationRules}
          activeScriptTab={activeScriptTab}
          setActiveScriptTab={setActiveScriptTab}
          scViewMode={scViewMode}
          setScViewMode={setScViewMode}
          scReviewSummary={scReviewSummary}
          scType={scType}
          scGenerating={scGenerating}
          handleRunQuery={handleRunQuery}
          scQueryResult={scQueryResult}
          scQueryRunning={scQueryRunning}
          scMappingDocs={scMappingDocs}
          scSelectedMappings={scSelectedMappings}
          onFixWithAI={handleFixWithAI}
          scPendingChange={scPendingChange}
          scAiPanelOpen={scAiPanelOpen}
          setScAiPanelOpen={setScAiPanelOpen}
        />

        {scAiPanelOpen && (
          <button
            type="button"
            className="ai-resizer"
            aria-label="Resize AI sidebar"
            onMouseDown={startAiResizing}
          />
        )}

        <div style={{ width: scAiPanelOpen ? scAiSidebarWidth : 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0, transition: 'width 0.3s ease' }}>
          <AIChat
            scAiInput={scAiInput} setScAiInput={setScAiInput}
            scAiMessages={scAiMessages} setScAiMessages={setScAiMessages}
            scAiLoading={scAiLoading}
            handleAiAlter={handleAiAlter}
            scPendingChange={scPendingChange}
            handleAcceptChange={handleAcceptChange}
            handleRejectChange={handleRejectChange}
            scAiPanelOpen={scAiPanelOpen}
            setScAiPanelOpen={setScAiPanelOpen}
          />
        </div>

      </div>

      {scGenerating && (
        <div className="generation-overlay">
          <div className="quantum-loader-card">
            <div className="loader-spinner-ring" />
            <div className="loader-header">
              <h3 className="loader-title">Generating your scripts...</h3>
              <p className="loader-subtext">This may take a few moments</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateScript;